/* eslint-disable @atlaskit/ui-styling-standard/enforce-style-prop */
/* eslint-disable @atlaskit/ui-styling-standard/no-classname-prop */
/* eslint-disable @atlaskit/ui-styling-standard/no-global-styles */
/**
 * @jsxRuntime classic
 * @jsx jsx
 * @jsxFrag React.Fragment
 */
import React from 'react';

import { jsx } from '@compiled/react';

import {
	LETTER_A_EDGES,
	LETTER_A_NODES,
	LETTER_A_OUTLINE_PATH,
	LETTER_A_VIEWBOX,
} from './letter-a-data';

const NODE_KEYFRAMES = 'twgHeroLetterANodeIn';
const EDGE_KEYFRAMES = 'twgHeroLetterAEdgeDraw';
const OUTLINE_KEYFRAMES = 'twgHeroLetterAOutlineDraw';
const NODE_CLASS = 'twg-hero-letter-a__node';
const EDGE_CLASS = 'twg-hero-letter-a__edge';
const OUTLINE_CLASS = 'twg-hero-letter-a__outline';

const NODE_REVEAL_MS = 220;
const EDGE_REVEAL_MS = 320;
const OUTLINE_PHASE_END = 0.7;
const EDGES_PHASE_START = 0.25;
const EDGES_PHASE_END = 0.85;
const DEFAULT_NODES_PHASE_START = 0.35;
const DEFAULT_NODES_PHASE_END = 1.0;

export type EaseControlPoints = [number, number, number, number];

const DEFAULT_EASE: EaseControlPoints = [0.34, 1.56, 0.64, 1.0];

function makeBezierEasing([x1, y1, x2, y2]: EaseControlPoints) {
	const bezX = (t: number) => {
		const u = 1 - t;
		return 3 * u * u * t * x1 + 3 * u * t * t * x2 + t * t * t;
	};
	const bezY = (t: number) => {
		const u = 1 - t;
		return 3 * u * u * t * y1 + 3 * u * t * t * y2 + t * t * t;
	};
	const bezDX = (t: number) => {
		const u = 1 - t;
		return 3 * u * u * x1 + 6 * u * t * (x2 - x1) + 3 * t * t * (1 - x2);
	};

	return (x: number) => {
		if (x <= 0) return 0;
		if (x >= 1) return 1;
		let t = x;
		for (let i = 0; i < 8; i++) {
			const xt = bezX(t);
			const dx = bezDX(t);
			if (dx === 0) break;
			const dt = (xt - x) / dx;
			t -= dt;
			if (Math.abs(dt) < 1e-6) break;
		}
		return bezY(t);
	};
}

function edgeLength(a: { x: number; y: number }, b: { x: number; y: number }) {
	return Math.hypot(b.x - a.x, b.y - a.y);
}

function mulberry32(seed: number) {
	let state = (seed | 0) + 0x6d2b79f5;
	return () => {
		state = (state + 0x6d2b79f5) | 0;
		let t = state;
		t = Math.imul(t ^ (t >>> 15), t | 1);
		t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
		return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
	};
}

function buildRevealOrder(count: number, shuffle: boolean, seed: number): number[] {
	const order = Array.from({ length: count }, (_, i) => i);
	if (!shuffle || count < 2) return order;
	const rand = mulberry32(seed);
	for (let i = count - 1; i > 0; i--) {
		const j = Math.floor(rand() * (i + 1));
		[order[i], order[j]] = [order[j], order[i]];
	}
	return order;
}

export type LetterAProps = {
	durationMs?: number;
	ease?: EaseControlPoints;
	strokeColor?: string;
	strokeWidth?: number;
	nodePhaseStart?: number;
	nodePhaseEnd?: number;
	shuffleNodes?: boolean;
	shuffleSeed?: number;
	reducedMotion?: boolean;
};

export function LetterA({
	durationMs = 1000,
	ease = DEFAULT_EASE,
	strokeColor = '#ffffff',
	strokeWidth = 2,
	nodePhaseStart = DEFAULT_NODES_PHASE_START,
	nodePhaseEnd = DEFAULT_NODES_PHASE_END,
	shuffleNodes = false,
	shuffleSeed = 0,
	reducedMotion = false,
}: LetterAProps): JSX.Element {
	const easeFn = makeBezierEasing(ease);
	const outlineWindowMs = reducedMotion ? 0 : Math.max(0, durationMs * OUTLINE_PHASE_END);
	const edgeWindowStartMs = reducedMotion ? 0 : durationMs * EDGES_PHASE_START;
	const edgeWindowMs = reducedMotion
		? 0
		: Math.max(0, durationMs * (EDGES_PHASE_END - EDGES_PHASE_START) - EDGE_REVEAL_MS);
	const nodeStart = Math.max(0, Math.min(1, nodePhaseStart));
	const nodeEnd = Math.max(nodeStart, Math.min(1, nodePhaseEnd));
	const nodeWindowStartMs = reducedMotion ? 0 : durationMs * nodeStart;
	const nodeWindowMs = reducedMotion
		? 0
		: Math.max(0, durationMs * (nodeEnd - nodeStart) - NODE_REVEAL_MS);
	const nodeCount = LETTER_A_NODES.length;
	const edgeCount = LETTER_A_EDGES.length;
	const revealOrder = buildRevealOrder(nodeCount, shuffleNodes, shuffleSeed);
	const revealPosition = new Array<number>(nodeCount);
	revealOrder.forEach((nodeIdx, position) => {
		revealPosition[nodeIdx] = position;
	});

	return (
		<>
			<style>{`
				@keyframes ${NODE_KEYFRAMES} {
					from { opacity: 0; transform: scale(0.4); }
					to { opacity: 1; transform: scale(1); }
				}
				@keyframes ${EDGE_KEYFRAMES} {
					to { stroke-dashoffset: 0; }
				}
				@keyframes ${OUTLINE_KEYFRAMES} {
					to { stroke-dashoffset: 0; }
				}
				.${NODE_CLASS} {
					opacity: ${reducedMotion ? '1' : '0'};
					transform-box: fill-box;
					transform-origin: center;
					animation: ${NODE_KEYFRAMES} ${reducedMotion ? 0 : NODE_REVEAL_MS}ms cubic-bezier(0.34, 1.56, 0.64, 1) forwards;
				}
				.${EDGE_CLASS} {
					animation: ${EDGE_KEYFRAMES} ${reducedMotion ? 0 : EDGE_REVEAL_MS}ms ease-out forwards;
				}
				.${OUTLINE_CLASS} {
					stroke-dasharray: 1;
					stroke-dashoffset: ${reducedMotion ? 0 : 1};
					animation: ${OUTLINE_KEYFRAMES} ${outlineWindowMs}ms ease-in-out forwards;
				}
			`}</style>
			<svg
				viewBox={`${LETTER_A_VIEWBOX.x} ${LETTER_A_VIEWBOX.y} ${LETTER_A_VIEWBOX.width} ${LETTER_A_VIEWBOX.height}`}
				xmlns="http://www.w3.org/2000/svg"
				fill="none"
				aria-hidden
				style={{ width: '100%', height: '100%' }}
			>
				<path
					d={LETTER_A_OUTLINE_PATH}
					stroke={strokeColor}
					strokeWidth={strokeWidth}
					pathLength={1}
					className={OUTLINE_CLASS}
				/>
				<g>
					{LETTER_A_EDGES.map(([i, j], idx) => {
						const a = LETTER_A_NODES[i];
						const b = LETTER_A_NODES[j];
						const len = edgeLength(a, b);
						const t = edgeCount <= 1 ? 0 : idx / (edgeCount - 1);
						const delay = edgeWindowStartMs + easeFn(t) * edgeWindowMs;
						return (
							<line
								key={`e-${idx}`}
								x1={a.x}
								y1={a.y}
								x2={b.x}
								y2={b.y}
								stroke={strokeColor}
								strokeWidth={strokeWidth}
								strokeLinecap="round"
								strokeLinejoin="bevel"
								className={EDGE_CLASS}
								style={{
									strokeDasharray: len,
									strokeDashoffset: reducedMotion ? 0 : len,
									animationDelay: `${delay}ms`,
								}}
							/>
						);
					})}
				</g>
				<g>
					{LETTER_A_NODES.map((node, idx) => {
						const position = revealPosition[idx] ?? idx;
						const t = nodeCount <= 1 ? 0 : position / (nodeCount - 1);
						const delay = nodeWindowStartMs + easeFn(t) * nodeWindowMs;
						return (
							<circle
								key={`n-${idx}`}
								cx={node.x}
								cy={node.y}
								r={node.r}
								fill={node.color}
								className={NODE_CLASS}
								style={{ animationDelay: `${delay}ms` }}
							/>
						);
					})}
				</g>
			</svg>
		</>
	);
}
